import {
  GatewayConformite,
  GatewayGarantieFinanciere,
  GatewayQuestionnaire,
} from "@conformite/gateway";
import { QuestionnaireForm } from "@src/components/questionnaire/QuestionnaireForm/QuestionnaireForm";
import { QuestionnairePrefill } from "@src/components/questionnaire/QuestionnaireForm/useQuestionnaireForm";
import { UnreadDocumentsNotice } from "@src/components/controle-quinquennal/UnreadDocumentsNotice/UnreadDocumentsNotice";
import { QuestionnaireHistory } from "@src/components/questionnaire/QuestionnaireHistory/QuestionnaireHistory";
import { useCQAxeWithFilesPaginatedQuery } from "@src/data/controle-quinquennal/hook/useCQAxeWithFilesQuery";
import { useGarantieFinanciereAdminQuery } from "@src/data/garantie-financiere/hook/useGarantieFinanciereAdminQuery";
import { isRecette } from "@src/helper/environnement.helper";
import { useMemo } from "react";
import styles from "../CQAxePage.module.scss";

const CONTEXT_TYPE = "CONTROLE_QUINQUENNAL_AXE";

const GF_SLUG_TO_CATEGORIE: Partial<
  Record<GatewayQuestionnaire.QuestionnaireSlug, GatewayConformite.IasIobsp>
> = {
  cq_gf_ias: "ias",
  cq_gf_iobsp: "iobsp",
};

const ENCAISSEMENT_FONDS_QUESTION_ORDER = 1;

function getEncaissementFondsPrefillLabel(
  garantieFinanciere:
    | GatewayGarantieFinanciere.ConformiteGarantieFinanciereIas
    | GatewayGarantieFinanciere.ConformiteGarantieFinanciereIobsp
    | null
    | undefined
): "Oui" | "Non" | "Manquant" {
  const encaissementFonds = garantieFinanciere?.encaissementFonds;
  if (encaissementFonds !== null && encaissementFonds !== undefined)
    return encaissementFonds ? "Oui" : "Non";
  if (garantieFinanciere?.hasGarantieFinanciere === false) return "Non";
  return "Manquant";
}

export function CQAxeQuestionnaire({
  idCQ,
  idAxe,
  slug,
  siren,
  millesime,
  disabled,
  onSaved,
  onAnswersChange,
  onDirtyChange,
}: {
  idCQ: string;
  idAxe: string;
  slug?: GatewayQuestionnaire.QuestionnaireSlug;
  siren?: string;
  millesime?: number;
  disabled?: boolean;
  onSaved?: () => void;
  onAnswersChange?: (answers: Record<string, string>) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}) {
  const { data: queryResult } = useCQAxeWithFilesPaginatedQuery(idCQ, idAxe);

  const aiAnswersByQuestionId = useMemo(() => {
    if (!isRecette) return undefined;
    const answers = queryResult?.axe?.aiResult?.answers;
    if (!answers) return undefined;
    return new Map(answers.map((answer) => [answer.questionId, answer]));
  }, [queryResult?.axe?.aiResult]);

  const categorie = slug ? GF_SLUG_TO_CATEGORIE[slug] : undefined;
  const { data: garantieFinanciere, isLoading: isGarantieFinanciereLoading } =
    useGarantieFinanciereAdminQuery(siren, millesime, categorie);
  const prefill = useMemo<QuestionnairePrefill[] | undefined>(() => {
    if (!categorie) return undefined;
    return [
      {
        questionOrder: ENCAISSEMENT_FONDS_QUESTION_ORDER,
        optionLabel: getEncaissementFondsPrefillLabel(garantieFinanciere),
      },
    ];
  }, [categorie, garantieFinanciere]);

  return (
    <div className={styles.content}>
      {isRecette && (
        <UnreadDocumentsNotice aiResult={queryResult?.axe?.aiResult} />
      )}
      {slug ? (
        <>
          <QuestionnaireHistory contextType={CONTEXT_TYPE} contextId={idAxe} />
          <QuestionnaireForm
            slug={slug}
            contextType={CONTEXT_TYPE}
            contextId={idAxe}
            title="Contrôle quinquennal en cours"
            disabled={disabled}
            onSaved={onSaved}
            onAnswersChange={onAnswersChange}
            onDirtyChange={onDirtyChange}
            aiAnswersByQuestionId={aiAnswersByQuestionId}
            prefill={prefill}
            isPrefillLoading={Boolean(categorie) && isGarantieFinanciereLoading}
          />
        </>
      ) : (
        <p className={styles.noQuestionnaire}>
          Aucun questionnaire disponible pour cet axe.
        </p>
      )}
    </div>
  );
}
